import { ShieldAlert, AlertTriangle, Mail, ExternalLink, CheckCircle, FileText, FilePenLine } from "lucide-react";
import { Link } from "wouter";

export default function ClinicalSafetyPage() {
  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center h-16 gap-3">
          <ShieldAlert className="h-6 w-6 text-amber-500 shrink-0" />
          <span className="text-xl font-bold text-slate-800 tracking-tight">RheumHub</span>
          <span className="text-slate-300 hidden sm:block">·</span>
          <span className="text-sm text-slate-500 hidden sm:block">Clinical Safety</span>
          <div className="ml-auto">
            <Link href="/" className="text-sm text-teal-700 hover:text-teal-900 font-medium">← Back to RheumHub</Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 bg-amber-50 rounded-xl">
              <ShieldAlert className="h-7 w-7 text-amber-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Clinical Safety Information</h1>
              <p className="text-slate-500 mt-1">DCB0129 Clinical Risk Management — RheumHub Platform</p>
            </div>
          </div>
          <p className="text-slate-600 leading-relaxed max-w-3xl">
            <strong>Medantix Health Ltd</strong> is committed to patient safety and the responsible development of health information technology. This page sets out how we manage clinical risk in accordance with NHS Digital's DCB0129 standard, who our Clinical Safety Officer is, the intended purpose of the system, and how to report a safety concern.
          </p>
        </div>

        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="h-5 w-5 text-slate-500 shrink-0" />
            <h2 className="text-xl font-bold text-slate-900">Intended Purpose</h2>
          </div>
          <p className="text-slate-700 leading-relaxed">
            RheumHub is a <strong>clinical decision-support and health information platform</strong> for UK rheumatology healthcare professionals and patients. It is designed to:
          </p>
          <ul className="space-y-2 text-slate-700 text-sm leading-relaxed list-disc list-inside ml-1">
            <li>Provide healthcare professionals (nurses, doctors, pharmacists, physiotherapists, occupational therapists) with convenient access to summarised clinical guidelines and reference information from NICE, BSR, EULAR, RCN, and other recognised bodies.</li>
            <li>Support patients in understanding their rheumatic conditions, medications, and available UK resources in plain language.</li>
            <li>Provide AI-assisted clinical decision support and patient guidance, clearly labelled and grounded in published guidelines.</li>
          </ul>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
            <strong>Important limitation:</strong> RheumHub is a supplementary reference tool. It is not a medical device, does not process real-time patient data, and does not replace clinical assessment, professional judgment, or the registered clinician's accountability. All clinical decisions remain the responsibility of the treating clinician.
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="h-5 w-5 text-teal-600 shrink-0" />
            <h2 className="text-xl font-bold text-slate-900">DCB0129 Compliance</h2>
          </div>
          <p className="text-slate-700 leading-relaxed text-sm">
            Medantix Health Ltd develops RheumHub with awareness of the <a href="https://digital.nhs.uk/data-and-information/information-standards/information-standards-and-data-collections-including-extractions/publications-and-notifications/standards-and-collections/dcb0129-clinical-risk-management-its-application-in-the-manufacture-of-health-it-systems" target="_blank" rel="noopener noreferrer" className="underline text-teal-700 hover:text-teal-900">DCB0129 Clinical Risk Management standard <ExternalLink className="inline h-3.5 w-3.5 mb-0.5" /></a>, which governs the manufacture of health IT systems in the NHS.
          </p>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            {[
              { label: "Hazard identification", detail: "We identify potential hazards that could arise from use or misuse of the platform." },
              { label: "Risk assessment", detail: "Each hazard is assessed for severity and likelihood, with mitigations applied as appropriate." },
              { label: "Clinical Safety Case", detail: "A Clinical Safety Case is maintained and reviewed by our Clinical Safety Officer." },
              { label: "Incident reporting", detail: "Safety incidents and near-misses are tracked and reviewed to drive continuous improvement." },
            ].map((item) => (
              <div key={item.label} className="bg-slate-50 rounded-xl p-4">
                <p className="font-semibold text-slate-800 mb-1">{item.label}</p>
                <p className="text-slate-600">{item.detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <ShieldAlert className="h-5 w-5 text-amber-500 shrink-0" />
            <h2 className="text-xl font-bold text-slate-900">Clinical Safety Officer (CSO)</h2>
          </div>
          <p className="text-slate-700 leading-relaxed text-sm">
            Our Clinical Safety Officer is a qualified healthcare professional responsible for overseeing clinical risk management activities in accordance with DCB0129. The CSO:
          </p>
          <ul className="space-y-2 text-slate-700 text-sm list-disc list-inside ml-1">
            <li>Reviews and approves the Clinical Safety Case before major releases</li>
            <li>Investigates all reported safety incidents and near-misses</li>
            <li>Ensures clinical hazards are identified, assessed, and mitigated</li>
            <li>Maintains oversight of AI system prompts and guideline content currency</li>
          </ul>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm">
            <p className="font-semibold text-slate-700 mb-1">Contact the CSO</p>
            <a href="mailto:safety@medantixhealth.co.uk" className="inline-flex items-center gap-1.5 text-teal-700 hover:text-teal-900 font-medium underline">
              <Mail className="h-4 w-4 shrink-0" />
              safety@medantixhealth.co.uk
            </a>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
            <h2 className="text-xl font-bold text-slate-900">Reporting a Safety Concern</h2>
          </div>
          <p className="text-slate-700 text-sm leading-relaxed">
            If you identify any content on RheumHub that you believe may be clinically incorrect, outdated, or potentially harmful, or if you experience an adverse event related to use of this platform, please report it as soon as possible.
          </p>
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-5 flex items-start gap-4">
            <div className="p-2 bg-teal-100 rounded-lg shrink-0">
              <FilePenLine className="h-5 w-5 text-teal-700" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-teal-900 mb-1">Report in-app (HCPs)</p>
              <p className="text-teal-700 text-sm mb-3">
                Logged-in healthcare professionals can submit a report directly — your concern is stored in an audit trail and the CSO is notified immediately.
              </p>
              <Link
                href="/hcp/safety-report"
                className="inline-flex items-center gap-2 bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
              >
                <FilePenLine className="h-4 w-4" />
                Report a safety concern
              </Link>
            </div>
          </div>

          <div className="space-y-3 text-sm">
            <div className="flex gap-3 items-start">
              <Mail className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-800">Email the Clinical Safety Officer</p>
                <a href="mailto:safety@medantixhealth.co.uk" className="text-teal-700 hover:text-teal-900 underline">safety@medantixhealth.co.uk</a>
                <p className="text-slate-500 mt-0.5">Include: the content or feature involved, a description of the concern, and any patient safety implications.</p>
              </div>
            </div>
            <div className="flex gap-3 items-start">
              <ExternalLink className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-800">MHRA Yellow Card</p>
                <p className="text-slate-600">For concerns related to medication information or suspected adverse drug reactions, report via the <a href="https://yellowcard.mhra.gov.uk/" target="_blank" rel="noopener noreferrer" className="underline text-teal-700 hover:text-teal-900">MHRA Yellow Card scheme</a>.</p>
              </div>
            </div>
            <div className="flex gap-3 items-start">
              <ExternalLink className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-800">NHS Serious Incident Framework</p>
                <p className="text-slate-600">Incidents that result in or could have resulted in serious patient harm should also be reported through your organisation's standard incident reporting procedures.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7 space-y-4">
          <h2 className="text-xl font-bold text-slate-900">AI Safety Measures</h2>
          <p className="text-slate-700 text-sm leading-relaxed">
            RheumHub uses AI language models to provide clinical decision support and patient guidance. The following safeguards are in place:
          </p>
          <ul className="space-y-2 text-slate-700 text-sm list-disc list-inside ml-1">
            <li>All AI responses include mandatory refusal rules that prevent the AI from providing individual patient diagnoses, prescriptions, or interpretation of specific investigation results.</li>
            <li>Patient-facing AI is instructed to detect signs of crisis (including suicidal ideation, self-harm, and domestic abuse) and immediately signpost to emergency services and UK crisis helplines.</li>
            <li>Clinical AI prompts are grounded in named, current UK guidelines (NICE, BSR, NMC Code, GMC, GPhC, CSP, RCOT) and are reviewed with each platform update.</li>
            <li>All AI interactions are logged in an audit trail accessible to the CSO for review.</li>
            <li>AI responses are labelled throughout the platform as decision support only, not medical advice.</li>
          </ul>
        </section>

        <div className="text-xs text-slate-500 pb-8 space-y-1">
          <p>RheumHub Clinical Safety Information — Last reviewed: May 2026</p>
          <p>Questions about this page? Contact <a href="mailto:safety@medantixhealth.co.uk" className="underline">safety@medantixhealth.co.uk</a></p>
        </div>
      </main>
    </div>
  );
}
